import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function toQueryString(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v != null && v !== "",
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(entries.map(([k, v]) => [k, String(v)]));
  return `?${qs.toString()}`;
}

// ---------------------------------------------------------------------------
// Segments
// ---------------------------------------------------------------------------

// --- V2 segment types (canonical 6 + RFM 11) ---
export type CanonicalSegmentKey =
  | "new" | "active" | "vip" | "wholesale" | "at_risk" | "churned"
  | "dormant" | "high_frequency" | "cod_only" | "mobile_money_only";

export type RfmSegmentKey =
  | "champions" | "fideles" | "potentiels_fideles" | "nouveaux" | "prometteurs"
  | "a_reconquerir" | "a_risque" | "presque_inactifs" | "a_surveiller"
  | "perdus" | "inactifs";

export interface CanonicalSegmentRow {
  readonly key: CanonicalSegmentKey;
  readonly name: string;
  readonly description: string;
  readonly customer_count: number;
  readonly avg_revenue_fcfa: number;
  readonly total_revenue_fcfa: number;
}

export interface RfmSegmentRow {
  readonly key: RfmSegmentKey;
  readonly name: string;
  readonly description: string;
  readonly customer_count: number;
  readonly avg_revenue_fcfa: number;
}

export interface FeaturedSegmentRow {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly criteria: Record<string, unknown>;
  readonly customer_count: number;
  readonly avg_revenue_fcfa: number;
}

export interface SegmentsV2Response {
  readonly canonical: readonly CanonicalSegmentRow[];
  readonly rfm: readonly RfmSegmentRow[];
  readonly featured: readonly FeaturedSegmentRow[];
  readonly total_customers: number;
  readonly source: "materialized" | "live";
  readonly refreshed_at: string | null;
}

/** @deprecated Use CanonicalSegmentKey instead */
export type SegmentKey = CanonicalSegmentKey;

/** @deprecated Use CanonicalSegmentRow instead */
export interface CustomerSegmentRow {
  readonly id: string;
  readonly key: SegmentKey;
  readonly name: string;
  readonly description: string;
  readonly customer_count: number;
  readonly avg_revenue_fcfa: number;
}

/** @deprecated Use SegmentsV2Response instead */
export interface SegmentsResponse {
  readonly segments: readonly CustomerSegmentRow[];
}

export interface SegmentCustomer {
  readonly id: string;
  readonly full_name: string;
  readonly phone: string;
  readonly total_spent_fcfa: number;
  readonly last_order_date: string | null;
}

export interface SegmentCustomersResponse {
  readonly customers: readonly SegmentCustomer[];
  readonly segment: CustomerSegmentRow;
}

// ---------------------------------------------------------------------------
// Loyalty
// ---------------------------------------------------------------------------

export type LoyaltyTier = "bronze" | "silver" | "gold";

export interface LoyaltyMember {
  readonly id: string;
  readonly customer_id: string;
  readonly full_name: string;
  readonly phone: string;
  readonly tier: LoyaltyTier;
  readonly points_balance: number;
  readonly total_earned: number;
  readonly last_order_date: string | null;
}

export interface LoyaltyListResponse {
  readonly members: readonly LoyaltyMember[];
  readonly total_points_in_circulation: number;
  readonly total_members: number;
  readonly points_fcfa_value: number;
}

export interface LoyaltyConfig {
  readonly earn_rate_fcfa_per_point: number;
  readonly burn_rate_points_per_fcfa: number;
  readonly tier_bronze_min: number;
  readonly tier_silver_min: number;
  readonly tier_gold_min: number;
}

export interface LoyaltyAdjustPayload {
  readonly customer_id: string;
  readonly points_delta: number;
  readonly reason: string;
}

export interface LoyaltyAdjustResponse {
  readonly new_balance: number;
  readonly tier: LoyaltyTier;
}

// ---------------------------------------------------------------------------
// WhatsApp
// ---------------------------------------------------------------------------

export interface WhatsAppTemplate {
  readonly id: string;
  readonly name: string;
  readonly body: string;
  readonly has_image: boolean;
  readonly created_at: string;
}

export interface WhatsAppBroadcastPayload {
  readonly segment_key: SegmentKey;
  readonly template_id: string;
  readonly message_override?: string;
  readonly image_url?: string;
  readonly scheduled_at?: string | null;
}

export interface WhatsAppBroadcastRecord {
  readonly id: string;
  readonly segment_key: SegmentKey;
  readonly segment_name: string;
  readonly message_preview: string;
  readonly sent_count: number;
  readonly delivered_count: number;
  readonly read_count: number;
  readonly scheduled_at: string | null;
  readonly created_at: string;
}

export interface WhatsAppHistoryResponse {
  readonly broadcasts: readonly WhatsAppBroadcastRecord[];
}

export interface WhatsAppTemplatesResponse {
  readonly templates: readonly WhatsAppTemplate[];
}

// ---------------------------------------------------------------------------
// SMS
// ---------------------------------------------------------------------------

export interface SMSTemplate {
  readonly id: string;
  readonly name: string;
  readonly body: string;
  readonly merge_fields: readonly string[];
}

export interface SMSCampaignPayload {
  readonly segment_key: SegmentKey;
  readonly template_id: string;
  readonly message_override?: string;
  readonly scheduled_at?: string | null;
}

export interface SMSCampaignRecord {
  readonly id: string;
  readonly segment_key: SegmentKey;
  readonly segment_name: string;
  readonly message_preview: string;
  readonly recipient_count: number;
  readonly sent_count: number;
  readonly delivered_count: number;
  readonly scheduled_at: string | null;
  readonly status: "draft" | "scheduled" | "sending" | "sent" | "failed";
  readonly created_at: string;
}

export interface SMSCampaignsResponse {
  readonly campaigns: readonly SMSCampaignRecord[];
}

export interface SMSTemplatesResponse {
  readonly templates: readonly SMSTemplate[];
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export interface FeedbackItem {
  readonly id: string;
  readonly customer_id: string;
  readonly customer_name: string;
  readonly rating: 1 | 2 | 3 | 4 | 5;
  readonly comment: string | null;
  readonly order_id: string;
  readonly delivery_date: string;
  readonly submitted_at: string;
}

export interface FeedbackListResponse {
  readonly feedback: readonly FeedbackItem[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

export interface FeedbackSummary {
  readonly avg_rating: number;
  readonly nps_estimate: number;
  readonly total_reviews: number;
  readonly response_rate_percent: number;
  readonly rating_distribution: Record<string, number>;
}

export interface FeedbackListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly rating?: number | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

// ---------------------------------------------------------------------------
// Abandoned Carts
// ---------------------------------------------------------------------------

export type CartStatus = "abandoned" | "recovered" | "expired";

export interface AbandonedCartItem {
  readonly id: string;
  readonly customer_id: string;
  readonly customer_name: string;
  readonly customer_phone: string;
  readonly item_count: number;
  readonly total_value_fcfa: number;
  readonly abandoned_at: string;
  readonly reminder_sent_at: string | null;
  readonly status: CartStatus;
  readonly recovered_revenue_fcfa: number | null;
}

export interface AbandonedCartsResponse {
  readonly carts: readonly AbandonedCartItem[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

export interface AbandonedCartsSummary {
  readonly total_abandoned: number;
  readonly total_recovered: number;
  readonly recovery_rate_percent: number;
  readonly recovered_revenue_fcfa: number;
}

export interface AbandonedCartsListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly status?: CartStatus | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

// ---------------------------------------------------------------------------
// Customer Lifetime Value
// ---------------------------------------------------------------------------

export interface CLVCustomer {
  readonly id: string;
  readonly customer_id: string;
  readonly full_name: string;
  readonly phone: string;
  readonly historical_clv_fcfa: number;
  readonly predicted_clv_12m_fcfa: number;
  readonly avg_basket_fcfa: number;
  readonly order_frequency_per_month: number;
  readonly tier: LoyaltyTier;
}

export interface CLVListResponse {
  readonly customers: readonly CLVCustomer[];
  readonly avg_clv_fcfa: number;
  readonly top10_avg_clv_fcfa: number;
  readonly top10_revenue_share_percent: number;
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

export interface CLVListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly sort?: "historical_desc" | "predicted_desc" | "basket_desc";
}

export interface CustomerCLV {
  readonly customer_id: string;
  readonly full_name: string;
  readonly historical_clv_fcfa: number;
  readonly predicted_clv_12m_fcfa: number;
  readonly avg_basket_fcfa: number;
  readonly order_frequency_per_month: number;
  readonly tier: LoyaltyTier;
}

// ---------------------------------------------------------------------------
// Referrals
// ---------------------------------------------------------------------------

export interface ReferralSponsor {
  readonly id: string;
  readonly customer_id: string;
  readonly full_name: string;
  readonly phone: string;
  readonly referral_code: string;
  readonly referee_count: number;
  readonly converted_count: number;
  readonly rewards_earned_fcfa: number;
}

export interface ReferralsListResponse {
  readonly sponsors: readonly ReferralSponsor[];
  readonly total_referrals: number;
  readonly total_converted: number;
  readonly conversion_rate_percent: number;
  readonly total_revenue_generated_fcfa: number;
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

export interface ReferralConfig {
  readonly sponsor_reward_fcfa: number;
  readonly referee_reward_fcfa: number;
  readonly min_first_order_fcfa: number;
  readonly program_active: boolean;
}

export interface ReferralsListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly q?: string | null;
}

// ---------------------------------------------------------------------------
// Communications
// ---------------------------------------------------------------------------

export type CommunicationType =
  | "order"
  | "payment"
  | "whatsapp"
  | "sms"
  | "call"
  | "return"
  | "admin_note";

export interface CommunicationEntry {
  readonly id: string;
  readonly type: CommunicationType;
  readonly title: string;
  readonly body: string | null;
  readonly reference_id: string | null;
  readonly admin_user_name: string | null;
  readonly created_at: string;
}

export interface CommunicationsResponse {
  readonly communications: readonly CommunicationEntry[];
  readonly customer_name: string;
}

export interface AddNotePayload {
  readonly text: string;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const crmApi = {
  segments: {
    list: (queryString?: string) =>
      adminFetch<SegmentsV2Response>(`/api/admin/crm/segments${queryString ?? ""}`),

    customers: (segmentKey: SegmentKey) =>
      adminFetch<SegmentCustomersResponse>(
        `/api/admin/crm/segments/${encodeURIComponent(segmentKey)}/customers`,
      ),

    export: (segmentKey: SegmentKey) =>
      adminFetch<Blob>(
        `/api/admin/crm/segments/${encodeURIComponent(segmentKey)}/export`,
      ),

    create: (payload: { name: string; description?: string; criteria: Record<string, unknown> }) =>
      adminFetch<FeaturedSegmentRow>("/api/admin/crm/segments", {
        method: "POST",
        body: JSON.stringify(payload),
      }),

    remove: (id: string) =>
      adminFetch<{ deleted: boolean }>(`/api/admin/crm/segments?id=${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
  },

  loyalty: {
    list: () => adminFetch<LoyaltyListResponse>("/api/admin/crm/loyalty"),

    adjust: (payload: LoyaltyAdjustPayload) =>
      adminFetch<LoyaltyAdjustResponse>("/api/admin/crm/loyalty/adjust", {
        method: "POST",
        body: JSON.stringify(payload),
      }),

    config: {
      get: () => adminFetch<LoyaltyConfig>("/api/admin/crm/loyalty/config"),
      update: (config: Partial<LoyaltyConfig>) =>
        adminFetch<LoyaltyConfig>("/api/admin/crm/loyalty/config", {
          method: "PUT",
          body: JSON.stringify(config),
        }),
    },
  },

  whatsapp: {
    history: () =>
      adminFetch<WhatsAppHistoryResponse>("/api/admin/crm/whatsapp/broadcasts"),

    broadcast: (payload: WhatsAppBroadcastPayload) =>
      adminFetch<{ broadcast_id: string }>("/api/admin/crm/whatsapp/broadcast", {
        method: "POST",
        body: JSON.stringify(payload),
      }),

    templates: {
      list: () =>
        adminFetch<WhatsAppTemplatesResponse>(
          "/api/admin/crm/whatsapp/templates",
        ),
      create: (template: Omit<WhatsAppTemplate, "id" | "created_at">) =>
        adminFetch<WhatsAppTemplate>("/api/admin/crm/whatsapp/templates", {
          method: "POST",
          body: JSON.stringify(template),
        }),
    },
  },

  sms: {
    campaigns: {
      list: () => adminFetch<SMSCampaignsResponse>("/api/admin/crm/sms/campaigns"),
      create: (payload: SMSCampaignPayload) =>
        adminFetch<SMSCampaignRecord>("/api/admin/crm/sms/campaigns", {
          method: "POST",
          body: JSON.stringify(payload),
        }),
    },
    templates: {
      list: () =>
        adminFetch<SMSTemplatesResponse>("/api/admin/crm/sms/templates"),
    },
  },

  feedback: {
    list: (params: FeedbackListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        rating: params.rating,
        date_from: params.date_from,
        date_to: params.date_to,
      });
      return adminFetch<FeedbackListResponse>(`/api/admin/crm/feedback${qs}`);
    },
    summary: () =>
      adminFetch<FeedbackSummary>("/api/admin/crm/feedback/summary"),
  },

  abandonedCarts: {
    list: (params: AbandonedCartsListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        status: params.status,
        date_from: params.date_from,
        date_to: params.date_to,
      });
      return adminFetch<AbandonedCartsResponse>(
        `/api/admin/crm/abandoned-carts${qs}`,
      );
    },
    summary: () =>
      adminFetch<AbandonedCartsSummary>(
        "/api/admin/crm/abandoned-carts/summary",
      ),
  },

  clv: {
    list: (params: CLVListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        sort: params.sort ?? "historical_desc",
      });
      return adminFetch<CLVListResponse>(`/api/admin/crm/clv${qs}`);
    },
    customer: (customerId: string) =>
      adminFetch<CustomerCLV>(
        `/api/admin/crm/customers/${encodeURIComponent(customerId)}/clv`,
      ),
  },

  referrals: {
    list: (params: ReferralsListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        q: params.q,
      });
      return adminFetch<ReferralsListResponse>(
        `/api/admin/crm/referrals${qs}`,
      );
    },
    config: {
      get: () => adminFetch<ReferralConfig>("/api/admin/crm/referrals/config"),
      update: (config: Partial<ReferralConfig>) =>
        adminFetch<ReferralConfig>("/api/admin/crm/referrals/config", {
          method: "PUT",
          body: JSON.stringify(config),
        }),
    },
  },

  communications: {
    list: (customerId: string) =>
      adminFetch<CommunicationsResponse>(
        `/api/admin/crm/customers/${encodeURIComponent(customerId)}/communications`,
      ),
    addNote: (customerId: string, payload: AddNotePayload) =>
      adminFetch<CommunicationEntry>(
        `/api/admin/crm/customers/${encodeURIComponent(customerId)}/communications/notes`,
        { method: "POST", body: JSON.stringify(payload) },
      ),
  },
} as const;

// ---------------------------------------------------------------------------
// Customer list (enriched via customer_list_view)
// ---------------------------------------------------------------------------

// Matches the segments emitted by customer_list_view (migration 079) and the
// server-side filter allow-list in /api/admin/customers/route.ts.
export type CustomerListSegment =
  | "new"
  | "active"
  | "vip"
  | "at_risk"
  | "wholesale"
  | "churned";

export interface CustomerListRow {
  readonly id: string;
  readonly email: string;
  readonly phone: string;
  readonly full_name: string;
  readonly preferred_language: "fr" | "wo" | "en";
  readonly created_at: string;
  readonly total_orders: number;
  readonly total_spent_fcfa: number;
  readonly last_order_date: string | null;
  readonly loyalty_tier: "bronze" | "silver" | "gold" | "platinum";
  readonly loyalty_points: number;
  readonly region: string | null;
  readonly segment: CustomerListSegment;
}

export interface CustomerListResponse {
  readonly customers: readonly CustomerListRow[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

export interface CustomerListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly search?: string | null;
  /** "all" is equivalent to omitting the filter. */
  readonly segment?: CustomerListSegment | "all" | null;
  /** "Toutes" is equivalent to omitting the filter. */
  readonly region?: string | null;
}

// ---------------------------------------------------------------------------
// Customer stats (full-dataset KPIs for the Clients page header)
// ---------------------------------------------------------------------------

export interface CustomerStatsResponse {
  readonly total: number;
  readonly new_this_month: number;
  readonly avg_value_fcfa: number;
  /** Percent with one decimal, e.g. 62.4 */
  readonly retention_rate: number;
  readonly segment_counts: Readonly<Record<CustomerListSegment, number>>;
  readonly region_counts: Readonly<Record<string, number>>;
}

/**
 * Single-customer mutation payload.
 * Mirrors the server-side Zod schema at /api/admin/customers/[id]/route.ts.
 * Blocking requires `blocked_reason` (min 3 chars); unblocking sends only
 * `is_blocked: false` and the server clears the companion columns.
 */
export interface CustomerBlockInput {
  readonly is_blocked: boolean;
  readonly blocked_reason?: string;
}

export interface CustomerBlockState {
  readonly id: string;
  readonly is_blocked: boolean;
  readonly blocked_at: string | null;
  readonly blocked_by: string | null;
  readonly blocked_reason: string | null;
}

/**
 * Full customer detail shape from GET /api/admin/customers/:id. The route
 * reads from `customers_decrypted` so PII columns are plaintext here; keep
 * that in mind when logging.
 */
export interface CustomerDetailResponse {
  readonly id: string;
  readonly full_name: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly is_blocked: boolean;
  readonly blocked_at: string | null;
  readonly blocked_by: string | null;
  readonly blocked_reason: string | null;
  readonly created_at: string;
  readonly updated_at?: string;
  readonly [key: string]: unknown;
}

export const customersApi = {
  list: (params: CustomerListParams = {}): Promise<CustomerListResponse> => {
    const qs = toQueryString({
      page: params.page,
      limit: params.limit,
      search: params.search,
      segment: params.segment,
      region: params.region,
    });
    return adminFetch<CustomerListResponse>(`/api/admin/customers${qs}`);
  },
  stats: (): Promise<CustomerStatsResponse> =>
    adminFetch<CustomerStatsResponse>("/api/admin/customers/stats"),

  /** Full single-customer detail (includes block fields). */
  getDetail: (id: string): Promise<CustomerDetailResponse> =>
    adminFetch<CustomerDetailResponse>(
      `/api/admin/customers/${encodeURIComponent(id)}`,
    ),

  /** S-H9: block or unblock a customer. */
  updateBlock: (
    id: string,
    input: CustomerBlockInput,
  ): Promise<CustomerBlockState> =>
    adminFetch<CustomerBlockState>(
      `/api/admin/customers/${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        body: JSON.stringify(input),
      },
    ),

  notes: {
    list: (customerId: string): Promise<readonly CustomerNote[]> =>
      adminFetch<readonly CustomerNote[]>(
        `/api/admin/customers/${encodeURIComponent(customerId)}/notes`,
      ),
    add: (customerId: string, text: string): Promise<CustomerNote> =>
      adminFetch<CustomerNote>(
        `/api/admin/customers/${encodeURIComponent(customerId)}/notes`,
        {
          method: "POST",
          body: JSON.stringify({ text }),
        },
      ),
    remove: (customerId: string, noteId: string): Promise<{ success: true }> =>
      adminFetch<{ success: true }>(
        `/api/admin/customers/${encodeURIComponent(customerId)}/notes/${encodeURIComponent(noteId)}`,
        { method: "DELETE" },
      ),
  },

  /**
   * Merge a duplicate customer into this one. The `source` customer is
   * archived (is_blocked = true, reason = "merged into <primary>") and its
   * orders/addresses/notes/tags/abandoned-carts are re-pointed to this
   * primary. Returns a per-table count of rows moved.
   */
  merge: (
    primaryId: string,
    sourceId: string,
  ): Promise<CustomerMergeResponse> =>
    adminFetch<CustomerMergeResponse>(
      `/api/admin/customers/${encodeURIComponent(primaryId)}/merge`,
      {
        method: "POST",
        body: JSON.stringify({ source_id: sourceId }),
      },
    ),

  tags: {
    list: (customerId: string): Promise<readonly CustomerTag[]> =>
      adminFetch<readonly CustomerTag[]>(
        `/api/admin/customers/${encodeURIComponent(customerId)}/tags`,
      ),
    add: (
      customerId: string,
      tag: string,
    ): Promise<{ tag: string; customer_id: string }> =>
      adminFetch<{ tag: string; customer_id: string }>(
        `/api/admin/customers/${encodeURIComponent(customerId)}/tags`,
        {
          method: "POST",
          body: JSON.stringify({ tag }),
        },
      ),
    remove: (
      customerId: string,
      tag: string,
    ): Promise<{ success: true }> =>
      adminFetch<{ success: true }>(
        `/api/admin/customers/${encodeURIComponent(customerId)}/tags?tag=${encodeURIComponent(tag)}`,
        { method: "DELETE" },
      ),
  },
} as const;

export interface CustomerTag {
  readonly tag: string;
  readonly added_at: string;
  readonly added_by: string | null;
}

export interface CustomerMergeResponse {
  readonly primary_id: string;
  readonly source_id: string;
  readonly moved: Record<string, number | string>;
  readonly warnings: readonly string[];
  readonly archived: boolean;
}

export interface CustomerNote {
  readonly id: string;
  readonly customer_id: string;
  readonly admin_user_id: string;
  readonly text: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly admin?: { full_name: string } | null;
}
